'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import api from '@/lib/axios';
import useAuthStore from '@/store/auth.store';
import Badge from '@/components/ui/Badge';
import { ShoppingBag, ChevronRight, Package } from 'lucide-react';
import Button from '@/components/ui/Button';

interface Siparis {
  id: number;
  siparis_no: string;
  durum: string;
  genel_toplam: number;
  created_at: string;
  siparis_kalemleri: { urun_adi: string; miktar: number; toplam_fiyat: number }[];
}

const durumVariant: Record<string, 'warning' | 'primary' | 'secondary' | 'success' | 'error' | 'gray'> = {
  beklemede: 'warning',
  odeme_alindi: 'primary',
  hazirlaniyor: 'secondary',
  kargoda: 'primary',
  teslim_edildi: 'success',
  iptal: 'error',
  iade_talep: 'warning',
  iade_tamamlandi: 'gray',
};

const durumLabel: Record<string, string> = {
  beklemede: '⏳ Beklemede',
  odeme_alindi: '💳 Ödeme Alındı',
  hazirlaniyor: '📦 Hazırlanıyor',
  kargoda: '🚚 Kargoda',
  teslim_edildi: '✅ Teslim Edildi',
  iptal: '❌ İptal',
  iade_talep: '↩️ İade Talebi',
  iade_tamamlandi: '✔️ İade Tamamlandı',
};

export default function SiparislerimPage() {
  const router = useRouter();
  const { girisYapildi } = useAuthStore();
  const [siparisler, setSiparisler] = useState<Siparis[]>([]);
  const [yukleniyor, setYukleniyor] = useState(true);

  useEffect(() => {
    if (!girisYapildi) {
      router.push('/giris');
      return;
    }
    api.get('/siparisler').then((res) => {
      setSiparisler(res.data.veri || []);
      setYukleniyor(false);
    }).catch(() => setYukleniyor(false));
  }, [girisYapildi]);

  if (yukleniyor) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-6">Siparişlerim</h1>
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-[var(--color-gray-200)] animate-pulse rounded-[var(--radius-xl)] h-36" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-6">
        <ShoppingBag className="w-6 h-6 text-[var(--color-primary)]" />
        <h1 className="text-2xl font-bold text-[var(--color-gray-800)]">Siparişlerim</h1>
        {siparisler.length > 0 && (
          <span className="text-sm text-[var(--color-gray-500)]">({siparisler.length} sipariş)</span>
        )}
      </div>

      {siparisler.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-24 h-24 bg-[var(--color-gray-100)] rounded-full flex items-center justify-center mx-auto mb-4">
            <Package className="w-12 h-12 text-[var(--color-gray-400)]" />
          </div>
          <p className="text-[var(--color-gray-500)] mb-6 text-lg">Henüz siparişiniz bulunmuyor</p>
          <Link href="/urunler">
            <Button size="lg">Alışverişe Başla</Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {siparisler.map((siparis) => (
            <div
              key={siparis.id}
              className="bg-white rounded-[var(--radius-xl)] shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-md)] transition p-6"
            >
              {/* Başlık */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="font-bold text-[var(--color-gray-800)] text-lg">{siparis.siparis_no}</p>
                  <p className="text-sm text-[var(--color-gray-500)] mt-0.5">
                    {new Date(siparis.created_at).toLocaleDateString('tr-TR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </p>
                </div>
                <Badge variant={durumVariant[siparis.durum] || 'gray'}>
                  {durumLabel[siparis.durum] || siparis.durum}
                </Badge>
              </div>

              {/* Kalemler */}
              <div className="border-t border-[var(--color-gray-100)] pt-4 mb-4 space-y-1.5">
                {siparis.siparis_kalemleri?.slice(0, 3).map((kalem, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-[var(--color-gray-600)]">
                      {kalem.urun_adi}
                      <span className="text-[var(--color-gray-400)] ml-1">×{kalem.miktar}</span>
                    </span>
                    <span className="font-medium">{kalem.toplam_fiyat?.toLocaleString('tr-TR')} ₺</span>
                  </div>
                ))}
                {siparis.siparis_kalemleri?.length > 3 && (
                  <p className="text-xs text-[var(--color-gray-400)]">
                    +{siparis.siparis_kalemleri.length - 3} ürün daha
                  </p>
                )}
              </div>

              {/* Alt */}
              <div className="flex items-center justify-between border-t border-[var(--color-gray-100)] pt-4">
                <div>
                  <span className="text-sm text-[var(--color-gray-500)]">Toplam: </span>
                  <span className="font-bold text-[var(--color-primary)] text-lg">
                    {siparis.genel_toplam?.toLocaleString('tr-TR')} ₺
                  </span>
                </div>
                <Link
                  href={`/siparislerim/${siparis.id}`}
                  className="flex items-center gap-1 text-sm text-[var(--color-primary)] hover:underline font-medium"
                >
                  Detayları Gör
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}